import { Link } from 'react-router-dom';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import type { NavAction, NavItem } from '@/app/nav';
import MotorPlanLogo from '@shared/components/MotorPlanLogo';
import { ButtonLink } from '@shared/components/ui/button-link/ButtonLink';
import { hasActiveSubItem } from './activeNavPath';
import SidebarSubItemList from './SidebarSubItemList';
import type { SidebarGroup } from './useSidebarNav';
import { submenuKey, type MenuType, type useSidebarSubmenu } from './useSidebarSubmenu';

type Submenu = ReturnType<typeof useSidebarSubmenu>;

interface SidebarFullProps {
    groups: SidebarGroup[];
    isActive: (path: string) => boolean;
    submenu: Submenu;
    showLogo: boolean;
    newQuote: NavAction | null;
}

interface SidebarNavItemProps {
    nav: NavItem;
    menuType: MenuType;
    isActive: (path: string) => boolean;
    submenu: Submenu;
}

function SidebarNavItem({ nav, menuType, isActive, submenu }: SidebarNavItemProps) {
    const key = submenuKey(menuType, nav.name);
    const isOpen = submenu.openKey === key;
    const highlighted = isOpen || hasActiveSubItem(nav, isActive);
    const panelId = `sidebar-submenu-${key}`;

    if (nav.subItems) {
        return (
            <li>
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => submenu.toggle(key)}
                    className={`menu-item group cursor-pointer ${highlighted ? 'menu-item-parent-active' : 'menu-item-inactive'}`}
                >
                    <span className={`menu-item-icon-size ${highlighted ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`}>
                        {nav.icon}
                    </span>
                    <span className="menu-item-text">{nav.name}</span>
                    <ChevronDownIcon
                        className={`ml-auto size-4 text-mp-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>
                <div
                    id={panelId}
                    ref={(el) => submenu.registerPanel(key, el)}
                    inert={!isOpen}
                    aria-hidden={!isOpen}
                    className="overflow-hidden transition-all duration-300"
                    style={{ height: isOpen ? `${submenu.panelHeight(key)}px` : '0px' }}
                >
                    <SidebarSubItemList items={nav.subItems} isActive={isActive} className="mt-0.5 mb-1.5 ml-5.25 flex flex-col gap-0.5 border-l border-mp-border pl-2.5" />
                </div>
            </li>
        );
    }

    if (!nav.path) return null;
    const active = isActive(nav.path);
    return (
        <li>
            <Link to={nav.path} className={`menu-item group ${active ? 'menu-item-active' : 'menu-item-inactive'}`}>
                <span className={`menu-item-icon-size ${active ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`}>
                    {nav.icon}
                </span>
                <span className="menu-item-text">{nav.name}</span>
            </Link>
        </li>
    );
}

/** The expanded sidebar: lockup, the New quote button and a scrolling menu under a fade. */
export default function SidebarFull({ groups, isActive, submenu, showLogo, newQuote }: SidebarFullProps) {
    return (
        <div className="flex h-full w-72.5 flex-col px-5">
            {showLogo && (
                <div className="flex py-5">
                    <Link to="/" aria-label="MotorPlan home">
                        <MotorPlanLogo textSize="h-7" />
                    </Link>
                </div>
            )}
            {newQuote && (
                <div className={`shrink-0 ${showLogo ? '' : 'pt-5'}`}>
                    <ButtonLink
                        to={newQuote.path}
                        variant="primary"
                        appearance="solid"
                        size="sm"
                        startIcon={<PlusIcon className="size-4" />}
                        className="w-full font-medium"
                    >
                        {newQuote.name}
                    </ButtonLink>
                </div>
            )}
            <div className="relative min-h-0 flex-1">
                <div className="no-scrollbar h-full overflow-y-auto">
                    <nav className="pb-8">
                        <div className="flex flex-col">
                            {groups.map((group) => (
                                <div key={group.type}>
                                    <h2 className="mt-6 mb-2 text-theme-2xs font-semibold uppercase text-mp-muted">
                                        {group.heading}
                                    </h2>
                                    <ul className="flex flex-col gap-1">
                                        {group.items.map((nav) => (
                                            <SidebarNavItem
                                                key={nav.name}
                                                nav={nav}
                                                menuType={group.type}
                                                isActive={isActive}
                                                submenu={submenu}
                                            />
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </nav>
                </div>
                <div
                    aria-hidden="true"
                    data-testid="sidebar-menu-fade"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-linear-to-t from-mp-surface"
                />
            </div>
        </div>
    );
}
